import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Plus, Briefcase, Trash, X as XIcon, ShieldCheck, Eye } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";

const TYPES = ["Individual", "Agency", "Vendor", "Lab", "Subcontractor"];
const KYC_STATUSES = ["not_verified", "pending_kyc", "kyc_verified", "rejected"];
const PAYMENT_METHODS = ["bank_transfer", "upi", "stripe", "cash"];
const COUNTRIES = ["IN", "BD", "PK", "SG", "AE", "EG", "CH", "UK", "US", "NP", "TH"];
const SPECIALIZATIONS = ["IP enforcement", "Field surveillance", "Test purchase", "Forensic lab",
                         "Legal counsel", "Digital forensics", "Translation", "Dark web monitoring"];

export default function Contractors() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [bankFor, setBankFor] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/contractors").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/contractors/service-types").then(({ data }) => setServiceTypes(data.service_types || [])).catch(() => {});
  }, []);

  const filtered = items.filter(c =>
    (!filterType || c.service_type === filterType) &&
    (!q || (c.full_name || "").toLowerCase().includes(q.toLowerCase())
        || (c.service_category || "").toLowerCase().includes(q.toLowerCase())
        || (c.gstin || "").toLowerCase().includes(q.toLowerCase())
        || (c.spoc || "").toLowerCase().includes(q.toLowerCase()))
  );

  const kycCls = (s) => s === "kyc_verified" ? "badge-success" : s === "rejected" ? "badge-danger" : s === "pending_kyc" ? "badge-info" : "";

  const remove = async (id) => {
    if (!window.confirm("Delete this supplier? Audit log is preserved.")) return;
    await api.delete(`/contractors/${id}`);
    load();
  };

  const viewBank = async (c) => {
    try {
      const { data } = await api.get(`/contractors/${c.contractor_id}/bank`);
      setBankFor({ ...c, ...data });
    } catch (e) {
      alert("Not allowed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-5" data-testid="contractors-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow">Finance</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Suppliers · {items.length}</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            KYC, encrypted bank details, service-type buckets, rating & payment history
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input placeholder="Search name / SPOC / GSTIN…" value={q} onChange={e => setQ(e.target.value)}
            data-testid="supplier-search"
            className="span-input h-9 text-[12px] w-[220px]"/>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            data-testid="supplier-filter-service-type"
            className="span-input h-9 text-[12px] w-[190px]">
            <option value="">All service types</option>
            {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button data-testid="add-contractor-btn" onClick={() => setShowAdd(true)} className="span-btn">
            <Plus size={14}/> Add supplier
          </button>
        </div>
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead>
            <tr>
              <th>Name</th><th>Service type</th><th>Specific service</th><th>SPOC</th>
              <th>Phone</th><th>GSTIN</th><th>KYC</th><th>Bank</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">No suppliers match the current filters.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.contractor_db_id || c.contractor_id} data-testid={`contractor-row-${c.contractor_id}`}>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{c.full_name || c.name || "—"}</span>
                    {c.is_primary_vendor && (
                      <span className="badge bg-emerald-50 text-emerald-800 border-emerald-200 text-[9px]" title="Verified from Vendor Master List">✓ VERIFIED</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--span-text-2)] font-mono">{c.contractor_id}</div>
                </td>
                <td><span className="badge">{c.service_type || "—"}</span></td>
                <td className="text-[11px] text-[var(--span-text-2)]">{c.service_category || "—"}</td>
                <td className="text-[11px]">{c.spoc || "—"}</td>
                <td className="font-mono text-[10px]">{c.phone || "—"}</td>
                <td className="font-mono text-[10px]">{c.gstin || "—"}</td>
                <td><span className={`badge ${kycCls(c.kyc_status)}`}>{c.kyc_status || "n/a"}</span></td>
                <td className="font-mono text-[10px]">
                  {c.account_number_masked ? (
                    <button data-testid={`bank-${c.contractor_id}`} onClick={() => viewBank(c)} className="hover:underline flex items-center gap-1">
                      <Eye size={11}/> {c.account_number_masked}
                    </button>
                  ) : "—"}
                </td>
                <td>
                  {!c.deleted_at ? (
                    <DeleteWorkflowButton
                      entityType="supplier"
                      entityId={c.contractor_id}
                      entityLabel={c.name}
                      onRequested={load}
                    />
                  ) : (
                    <span className="badge badge-danger text-[9px]">deleted</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {bankFor && <BankModal contractor={bankFor} onClose={() => setBankFor(null)} />}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: "", contractor_type: "Individual", country_code: "IN", city: "",
    phone: "", email: "", whatsapp_number: "", specialization: [],
    service_type: "", service_category: "", spoc: "", gstin: "", address: "",
    payment_method: "bank_transfer", bank_name: "",
    account_number: "", ifsc_swift_code: "", kyc_status: "not_verified",
  });
  const [serviceTypes, setServiceTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) => set("specialization", form.specialization.includes(s) ? form.specialization.filter((x) => x !== s) : [...form.specialization, s]);

  useEffect(() => {
    api.get("/contractors/service-types").then(({ data }) => setServiceTypes(data.service_types || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name) return;
    setBusy(true);
    try {
      await api.post("/contractors", form);
      onSaved();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="add-contractor-modal">
      <form onSubmit={submit} className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-1"><Briefcase size={12}/> New contractor</div>
          <button type="button" onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-1">Full name *</label>
            <input data-testid="contractor-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="span-input"/>
          </div>

          {/* ─── Type of Service (new — Feb 2026) ─── */}
          <div className="border border-[var(--span-border)] rounded p-3 bg-[var(--span-surface-2,#f9fafb)]">
            <div className="label-eyebrow mb-2 flex items-center gap-1">
              <Briefcase size={12}/> Type of service
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] block mb-1">Service category *</label>
                <select data-testid="service-type" value={form.service_type}
                  onChange={(e) => set("service_type", e.target.value)}
                  className="span-input">
                  <option value="">— Select category —</option>
                  {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] block mb-1">Specific service (free text)</label>
                <input data-testid="service-category" value={form.service_category}
                  onChange={(e) => set("service_category", e.target.value)}
                  placeholder="e.g. Pest Control · Rental Cars · Cleaner"
                  className="span-input"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] block mb-1">SPOC (single point of contact)</label>
                <input data-testid="spoc" value={form.spoc}
                  onChange={(e) => set("spoc", e.target.value)}
                  placeholder="Contact person name" className="span-input"/>
              </div>
              <div>
                <label className="text-[11px] block mb-1">GSTIN (optional)</label>
                <input data-testid="gstin" value={form.gstin}
                  onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                  placeholder="07AAACT2438A1ZV" className="span-input font-mono"/>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[11px] block mb-1">Address</label>
              <textarea data-testid="address" value={form.address}
                onChange={(e) => set("address", e.target.value)}
                rows={2} placeholder="Full postal address"
                className="span-input"/>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Type</label>
              <select value={form.contractor_type} onChange={(e) => set("contractor_type", e.target.value)} className="span-input">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Country</label>
              <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="span-input">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">City</label>
              <input value={form.city} onChange={(e) => set("city", e.target.value)} className="span-input"/>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Phone</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="span-input"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Email</label>
              <input value={form.email} onChange={(e) => set("email", e.target.value)} className="span-input"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">WhatsApp</label>
              <input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+91…" className="span-input"/>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Specializations</label>
            <div className="flex flex-wrap gap-1.5">
              {SPECIALIZATIONS.map((s) => (
                <button type="button" key={s} onClick={() => toggleSpec(s)}
                        className={`px-2 py-1 text-[11px] hairline ${form.specialization.includes(s) ? "bg-black text-white" : "surface"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-[var(--span-border)] pt-3 mt-3">
            <div className="label-eyebrow mb-2 flex items-center gap-1"><ShieldCheck size={12}/> Bank details (encrypted at rest)</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] block mb-1">Bank name</label>
                <input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="span-input"/>
              </div>
              <div>
                <label className="text-[11px] block mb-1">Account number</label>
                <input data-testid="account-number" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="span-input font-mono"/>
              </div>
              <div>
                <label className="text-[11px] block mb-1">IFSC / SWIFT</label>
                <input value={form.ifsc_swift_code} onChange={(e) => set("ifsc_swift_code", e.target.value)} className="span-input font-mono"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] block mb-1">Payment method</label>
                <select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="span-input">
                  {PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] block mb-1">KYC status</label>
                <select value={form.kyc_status} onChange={(e) => set("kyc_status", e.target.value)} className="span-input">
                  {KYC_STATUSES.map((k) => <option key={k}>{k}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button disabled={busy} data-testid="submit-contractor" className="span-btn"><Plus size={12}/> Save</button>
        </div>
      </form>
    </div>
  );
}

function BankModal({ contractor, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="bank-modal">
      <div className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-1"><ShieldCheck size={12}/> Bank details (decrypted view)</div>
          <button onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3 text-[13px]">
          <Row label="Supplier" value={contractor.full_name}/>
          <Row label="Bank" value={contractor.bank_name || "—"}/>
          <Row label="Account #" value={contractor.account_number || "—"} mono/>
          <Row label="IFSC / SWIFT" value={contractor.ifsc_swift_code || "—"} mono/>
        </div>
        <div className="mt-4 text-[10px] font-mono text-[var(--span-text-2)] uppercase tracking-wider">
          ACCESS LOGGED · Finance / Executive role only
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--span-text-2)]">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value}</span>
    </div>
  );
}
